import { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { validateMessage } from '../utils/profanityFilter';
import WantListCleanupModal from './WantListCleanupModal';

const FeedbackFormBody = ({ trade, tradeId, tradeType, currentUserId, buyerId, sellerId, onFeedbackSubmitted, shareLinks, onWantListPrompt }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [usernames, setUsernames] = useState({ fromUser: '', toUser: '' });

  const functions = getFunctions();
  const submitFeedbackFunction = httpsCallable(functions, 'submitFeedback');

  // Prewarm the callable while the member is still choosing a rating, so the
  // cold start is paid now rather than on Submit. Fire-and-forget.
  useEffect(() => {
    submitFeedbackFunction({ ping: true }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount
  }, []);

  // Determine user IDs based on trade type
  const fromUserId = tradeType === 'purchase' ? buyerId : trade?.fromUserId;
  const toUserId = tradeType === 'purchase' ? sellerId : trade?.toUserId;

  // Fetch usernames when component mounts
  useEffect(() => {
    const fetchUsernames = async () => {
      if (!fromUserId || !toUserId) return;

      try {
        const fromUserRef = doc(db, 'publicProfiles', fromUserId);
        const toUserRef = doc(db, 'publicProfiles', toUserId);

        const [fromSnap, toSnap] = await Promise.all([
          getDoc(fromUserRef),
          getDoc(toUserRef)
        ]);

        const fromUsername = fromSnap.exists() ? fromSnap.data().username || 'User A' : 'User A';
        const toUsername = toSnap.exists() ? toSnap.data().username || 'User B' : 'User B';

        setUsernames({ fromUser: fromUsername, toUser: toUsername });
      } catch (err) {
        console.error('Error fetching usernames for feedback:', err);
        setUsernames({ fromUser: 'User A', toUser: 'User B' });
      }
    };

    fetchUsernames();
  }, [fromUserId, toUserId]);

  // Determine if current user can submit feedback
  const isFromUser = fromUserId === currentUserId;
  const feedbackField = tradeType === 'purchase' 
    ? (isFromUser ? 'buyerFeedback' : 'sellerFeedback')
    : (isFromUser ? 'fromUserFeedback' : 'toUserFeedback');
  const hasSubmittedFeedback = trade?.[feedbackField];
  const canSubmitFeedback = trade?.status === 'complete' && !hasSubmittedFeedback;

  // Check if feedback should be visible
  const feedbackVisible = trade?.feedbackVisible;
  const fromUserFeedback = tradeType === 'purchase' ? trade?.buyerFeedback : trade?.fromUserFeedback;
  const toUserFeedback = tradeType === 'purchase' ? trade?.sellerFeedback : trade?.toUserFeedback;
  const bothSubmitted = fromUserFeedback && toUserFeedback;

  const handleStarClick = (starValue) => {
    if (canSubmitFeedback) {
      setRating(starValue);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!rating || rating < 1) {
      setError('Please select a rating');
      return;
    }

    const trimmedComment = comment.trim();
    if (trimmedComment) {
      const validation = validateMessage(trimmedComment);
      if (!validation.isValid) {
        setError(validation.error);
        return;
      }
    }

    setSubmitting(true);
    setError('');

    try {
      const result = await submitFeedbackFunction({
        tradeId: tradeId || trade?.id,
        tradeType: tradeType || 'trade',
        rating: rating,
        comment: comment.trim()
      });

      setSuccess(true);

      const prompts = result?.data?.wantListPrompt;
      if (Array.isArray(prompts) && prompts.length && onWantListPrompt) {
        onWantListPrompt(prompts);
      }

      if (onFeedbackSubmitted) {
        onFeedbackSubmitted();
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError(error.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (currentRating, interactive = false) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && handleStarClick(star)}
            className={`text-2xl transition-colors ${
              interactive && canSubmitFeedback
                ? 'cursor-pointer hover:scale-110'
                : 'cursor-default'
            } ${
              star <= currentRating
                ? 'text-yellow-500'
                : 'text-gray-300'
            }`}
            disabled={!interactive || !canSubmitFeedback}
            style={{
              color: star <= currentRating ? '#f59e0b' : '#d1d5db'
            }}
          >
            ★
          </button>
        ))}
      </div>
    );
  };

  // Render a single user's feedback column
  const renderUserFeedback = (username, feedback) => {
    return (
      <div style={{ textAlign: 'left' }}>
        <p style={{
          fontFamily: 'Nunito, sans-serif',
          fontWeight: '700',
          fontSize: '0.8rem',
          color: '#374151',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '6px'
        }}>
          {username || 'User'}
        </p>
        <div style={{ display: 'flex', gap: '2px', marginBottom: '4px' }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <span key={star} style={{ fontSize: '1.1rem', color: star <= feedback.rating ? '#C9A227' : '#d1d5db', lineHeight: 1 }}>★</span>
          ))}
        </div>
        <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#374151', fontWeight: '600', marginBottom: '6px' }}>
          {feedback.rating} / 5
        </p>
        {feedback.comment ? (
          <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#374151', fontStyle: 'italic', margin: 0, lineHeight: '1.4' }}>
            "{feedback.comment}"
          </p>
        ) : (
          <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#9ca3af', fontStyle: 'italic', margin: 0 }}>
            No comment left
          </p>
        )}
      </div>
    );
  };

  // If both users submitted feedback, show the feedback (regardless of feedbackVisible flag)
  if (bothSubmitted) {
    return (
      <div style={{ width: '100%' }}>
        <style jsx>{`
          @media (max-width: 768px) {
            .feedback-desktop-layout {
              display: none !important;
            }
            .feedback-mobile-layout {
              display: block !important;
            }
          }
          @media (min-width: 769px) {
            .feedback-mobile-layout {
              display: none !important;
            }
          }
        `}</style>

        {/* Desktop: 5-column grid with vertical dividers */}
        <div className="feedback-desktop-layout" style={{
          display: 'grid',
          gridTemplateColumns: '3fr 1px 2fr 1px 3fr',
          gap: '1rem',
          alignItems: 'stretch'
        }}>
          <div style={{ alignSelf: 'flex-start' }}>
            {renderUserFeedback(usernames.fromUser, fromUserFeedback)}
          </div>

          <div style={{ backgroundColor: '#e5e7eb' }} />

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
            <div style={{
              backgroundColor: '#e6f5f2',
              border: 'none',
              borderRadius: '9999px',
              padding: '8px 18px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap'
            }}>
              <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#4db6ac', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: '600', color: '#2e7d6e' }}>
                Both users submitted feedback
              </span>
            </div>
            <div style={{ marginTop: '6px' }}>
              {shareLinks}
            </div>
          </div>

          <div style={{ backgroundColor: '#e5e7eb' }} />

          <div style={{ alignSelf: 'flex-start' }}>
            {renderUserFeedback(usernames.toUser, toUserFeedback)}
          </div>
        </div>

        {/* Mobile: stacked layout */}
        <div className="feedback-mobile-layout" style={{ display: 'none' }}>
          {/* Badge at top */}
          <div style={{
            backgroundColor: '#e6f5f2',
            border: 'none',
            borderRadius: '9999px',
            padding: '8px 18px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '1rem'
          }}>
            <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#4db6ac', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: '600', color: '#2e7d6e' }}>
              Both users submitted feedback
            </span>
          </div>

          {/* Two columns with divider */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1px 1fr',
            gap: '1rem',
            borderTop: '1px solid #e5e7eb',
            paddingTop: '1rem'
          }}>
            {renderUserFeedback(usernames.fromUser, fromUserFeedback)}
            <div style={{ backgroundColor: '#e5e7eb' }} />
            {renderUserFeedback(usernames.toUser, toUserFeedback)}
          </div>

          {/* Share links */}
          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '1rem', paddingTop: '0.75rem', textAlign: 'center' }}>
            {shareLinks}
          </div>
        </div>
      </div>
    );
  }

  // If user already submitted feedback, show pending message
  if (hasSubmittedFeedback) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-blue-600">✓</span>
          <h3 className="text-lg font-semibold text-blue-800">Feedback Submitted</h3>
        </div>
        <p className="text-blue-700">
          Thank you for your feedback! It will be visible once both users have submitted their reviews.
        </p>
      </div>
    );
  }

  // If trade is not complete, show disabled message
  if (trade.status !== 'complete') {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Feedback</h3>
        <p className="text-gray-600">
          Feedback will be available once the trade is completed.
        </p>
      </div>
    );
  }

  // Show feedback form
  return (
    <div style={{ width: '100%', boxSizing: 'border-box' }}>
      <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: '700', fontSize: '1.1rem', color: '#1f2937', marginBottom: '1rem' }}>Leave feedback</h3>
      
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '4px', justifyContent: 'center' }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => canSubmitFeedback && handleStarClick(star)}
                disabled={!canSubmitFeedback}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0',
                  cursor: canSubmitFeedback ? 'pointer' : 'default',
                  fontSize: '2rem',
                  color: star <= rating ? '#C9A227' : '#d1d5db',
                  transition: 'color 0.15s ease, transform 0.15s ease',
                  transform: canSubmitFeedback && star <= rating ? 'scale(1.05)' : 'scale(1)',
                  lineHeight: 1
                }}
              >
                ★
              </button>
            ))}
          </div>
          <p style={{ fontSize: '0.8rem', color: '#9ca3af', fontFamily: 'Nunito, sans-serif', margin: 0 }}>
            {rating > 0 ? `${rating} of 5 selected` : 'Select a rating'}
          </p>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontFamily: 'Nunito, sans-serif', fontWeight: '600', fontSize: '0.9rem', color: '#374151', marginBottom: '6px' }}>
            Comments (optional)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your experience with this trade..."
            maxLength={500}
            rows={4}
            style={{ 
              padding: '12px',
              fontSize: '0.9rem',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              lineHeight: '1.5',
              fontFamily: 'Nunito, sans-serif',
              width: '100%',
              boxSizing: 'border-box',
              backgroundColor: '#ffffff',
              resize: 'vertical',
              outline: 'none'
            }}
            onFocus={(e) => { e.target.style.borderColor = '#C9A227'; }}
            onBlur={(e) => { e.target.style.borderColor = '#d1d5db'; }}
          />
          <p style={{ fontSize: '0.8rem', color: '#9ca3af', fontFamily: 'Nunito, sans-serif', textAlign: 'right', margin: '4px 0 0 0' }}>
            {500 - comment.length} remaining
          </p>
        </div>

        {error && (
          <div style={{ color: '#dc2626', fontSize: '0.85rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '8px 12px', marginBottom: '0.75rem' }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ color: '#166534', fontSize: '0.85rem', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '8px 12px', marginBottom: '0.75rem' }}>
            Feedback submitted successfully!
          </div>
        )}

        <p style={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Nunito, sans-serif', marginBottom: '0.75rem' }}>
          Your feedback stays hidden until both users have submitted.
        </p>

        <button
          type="submit"
          disabled={submitting || !rating}
          style={{
            width: '100%',
            padding: '10px 20px',
            borderRadius: '9999px',
            border: 'none',
            backgroundColor: submitting || !rating ? '#f3f4f6' : '#C9A227',
            fontFamily: 'Nunito, sans-serif',
            fontWeight: '700',
            fontSize: '0.9rem',
            color: submitting || !rating ? '#9ca3af' : 'white',
            cursor: submitting || !rating ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={e => { if (!submitting && rating) { e.currentTarget.style.opacity = '0.85'; }}}
          onMouseLeave={e => { if (!submitting && rating) { e.currentTarget.style.opacity = '1'; }}}
        >
          {submitting ? 'Submitting...' : 'Submit feedback'}
        </button>
      </form>
    </div>
  );
};

/**
 * Submitting feedback causes the parent to refetch, which flips the body over to
 * its "Feedback Submitted" branch. The Wantlist cleanup prompt has to outlive
 * that switch, so its state lives out here rather than inside the body.
 */
const FeedbackForm = (props) => {
  const [wantListPrompt, setWantListPrompt] = useState(null);

  return (
    <>
      <FeedbackFormBody {...props} onWantListPrompt={setWantListPrompt} />
      {wantListPrompt && (
        <WantListCleanupModal
          items={wantListPrompt}
          tradeType={props.tradeType || 'trade'}
          onClose={() => setWantListPrompt(null)}
        />
      )}
    </>
  );
};

export default FeedbackForm;
